// G.711 μ-law: 8-bit telephone-quality samples. Every runtime decodes it with a table, so the
// radio needs no codec library (Opus via WebCodecs is not in every WebView the staff use).
const BIAS = 0x84, CLIP = 32635;

export function encodeSample(pcm: number): number {
  let s = Math.max(-32768, Math.min(32767, Math.round(pcm)));
  const sign = s < 0 ? 0x80 : 0;
  if (sign) s = -s;
  if (s > CLIP) s = CLIP;
  s += BIAS;
  let exp = 7;
  for (let mask = 0x4000; (s & mask) === 0 && exp > 0; mask >>= 1) exp--;
  const mant = (s >> (exp + 3)) & 0x0f;
  return ~(sign | (exp << 4) | mant) & 0xff;
}

const DECODE = new Int16Array(256);
for (let i = 0; i < 256; i++) {
  const u = ~i & 0xff;
  const sign = u & 0x80, exp = (u >> 4) & 7, mant = u & 0x0f;
  const s = (((mant << 3) + BIAS) << exp) - BIAS;
  DECODE[i] = sign ? -s : s;
}
export const decodeSample = (b: number): number => DECODE[b & 0xff]!;

/** Float samples (-1..1) to μ-law bytes. */
export function encode(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = encodeSample(samples[i]! * 32767);
  return out;
}

/** μ-law bytes to float samples (-1..1). */
export function decode(bytes: Uint8Array): Float32Array<ArrayBuffer> {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = decodeSample(bytes[i]!) / 32768;
  return out;
}

/** Linear-interpolation resampler, enough for speech to 16 kHz. */
export function resample(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return input;
  const n = Math.floor((input.length * to) / from);
  const out = new Float32Array(n);
  const step = from / to;
  for (let i = 0; i < n; i++) {
    const x = i * step, j = Math.floor(x), f = x - j;
    out[i] = (input[j] ?? 0) * (1 - f) + (input[j + 1] ?? input[j] ?? 0) * f;
  }
  return out;
}
